import React, { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { SettingsButton, SettingsCard, SettingsChip, SettingsTitle } from '../../components/settings/SettingsUI'
import type { CreatorPlan } from '../../hooks/useCreatorTools'
import { useCreatorTools } from '../../hooks/useCreatorTools'

function FaqItem({
  q,
  a,
  open,
  onToggle,
}: {
  q: string
  a: React.ReactNode
  open: boolean
  onToggle: () => void
}) {
  return (
    <div className="rounded-xl border border-white/10 bg-black/20">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-4 py-3 flex items-center justify-between gap-4 text-left"
        aria-expanded={open}
      >
        <span className="font-semibold">{q}</span>
        <span className="text-white/60">{open ? '-' : '+'}</span>
      </button>
      {open ? <div className="px-4 pb-4 text-sm text-white/75 leading-relaxed">{a}</div> : null}
    </div>
  )
}

export default function BecomeCreator() {
  const DEV = import.meta.env.DEV
  const navigate = useNavigate()
  const { plan, devSetPlan } = useCreatorTools()
  const isCreator = plan !== 'none'
  const isPro = plan === 'creator_pro'

  const [notice, setNotice] = useState<string | null>(null)
  const [faqOpen, setFaqOpen] = useState<string | null>('vesting')

  const aiCredits = useMemo(() => {
    return {
      creator: 500,
      creator_pro: 2000,
    }
  }, [])

  const onChoose = (target: CreatorPlan) => {
    setNotice(null)
    if (target === 'none') return
    const tier = target === 'creator_pro' ? 'creator_pro' : 'creator'
    navigate(`/subscribe?tier=${tier}&interval=month`)
  }

  return (
    <div className="space-y-4">
      <SettingsTitle title="Become a Creator" subtitle="Unlock payouts, AI tools, and creator publishing features." />

      {isCreator ? (
        <SettingsCard accent="key" title="Current plan" subtitle={`You're currently on ${isPro ? 'Creator Pro' : 'Creator'}.`}>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-white/70">
              Manage billing, connect Stripe, and view vesting status from Pixe Publish.
            </div>
            <Link
              to="/settings/pixe-publish"
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-sky-600 text-white border-sky-500/40 hover:bg-sky-500 hover:border-sky-400/50"
            >
              Manage your plan
            </Link>
          </div>
        </SettingsCard>
      ) : null}

      <SettingsCard title="How payouts work">
        <ul className="space-y-1 text-sm text-white/80">
          <li>
            When people support your content with <span className="font-semibold">PIXE gifts</span>, it generates earnings.
          </li>
          <li>Earnings vest while your plan is active.</li>
          <li>Vested earnings can be withdrawn through Stripe.</li>
          <li className="text-white/70">PIXE gifts stay on Pixeloop - payouts are made in USD.</li>
        </ul>
      </SettingsCard>

      {notice ? (
        <div className="rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white/80">{notice}</div>
      ) : null}

      <div className="grid gap-4 md:grid-cols-2">
        <SettingsCard accent="key">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">Creator</div>
            <div className="text-sm font-semibold">
              $10<span className="text-white/60">/mo</span>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <SettingsChip variant="neutral">10 posts / month included</SettingsChip>
            <SettingsChip variant="vesting">12-month vesting</SettingsChip>
          </div>

          <ul className="mt-4 space-y-2 text-sm text-white/80 list-disc list-inside">
            <li>Unlock payouts via Stripe</li>
            <li>AI tools included</li>
            <li>Earnings vest while your plan is active</li>
          </ul>

          <div className="mt-5 flex flex-wrap gap-2">
            {plan === 'creator' ? (
              <SettingsButton disabled className="w-full sm:w-auto">
                Current plan
              </SettingsButton>
            ) : plan === 'creator_pro' ? (
              <SettingsButton
                variant="secondary"
                onClick={() => {
                  setNotice(null)
                  devSetPlan('creator').catch((e) => setNotice(e?.response?.data?.detail || e?.message || 'Update failed'))
                }}
                className="w-full sm:w-auto"
              >
                Downgrade to Creator
              </SettingsButton>
            ) : (
              <SettingsButton onClick={() => onChoose('creator')} className="w-full sm:w-auto">
                Become Creator
              </SettingsButton>
            )}
            {plan === 'creator_pro' ? (
              DEV ? <div className="text-xs text-white/60 self-center">Dev: downgrades apply immediately.</div> : null
            ) : null}
          </div>
        </SettingsCard>

        <SettingsCard accent="key">
          <div className="flex items-center justify-between">
            <div className="text-base font-semibold">Creator Pro</div>
            <div className="text-sm font-semibold">
              $25<span className="text-white/60">/mo</span>
            </div>
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <SettingsChip variant="available">Unlimited posts</SettingsChip>
            <SettingsChip variant="pro">6-month vesting</SettingsChip>
            <SettingsChip variant="pro">Faster vesting</SettingsChip>
          </div>

          <ul className="mt-4 space-y-2 text-sm text-white/80 list-disc list-inside">
            <li>Everything in Creator</li>
            <li>More AI tools + higher limits</li>
            <li>Unlimited posting</li>
            <li>Earnings vest while your plan is active</li>
          </ul>

          <div className="mt-5 flex flex-wrap gap-2">
            {plan === 'creator_pro' ? (
              <SettingsButton disabled className="w-full sm:w-auto">
                Current plan
              </SettingsButton>
            ) : plan === 'creator' ? (
              <SettingsButton onClick={() => onChoose('creator_pro')} className="w-full sm:w-auto">
                Upgrade to Pro
              </SettingsButton>
            ) : (
              <SettingsButton onClick={() => onChoose('creator_pro')} className="w-full sm:w-auto">
                Become Creator Pro
              </SettingsButton>
            )}
            <a
              href="#compare"
              className="inline-flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold transition border bg-white/10 text-white border-white/15 hover:bg-white/15 w-full sm:w-auto"
            >
              Compare
            </a>
          </div>
        </SettingsCard>
      </div>

      <SettingsCard title="Compare plans" subtitle="Launch limits may vary.">
        <div id="compare" />
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-white/60">
                <th className="py-2 pr-4 font-semibold">Feature</th>
                <th className="py-2 pr-4 font-semibold">Creator</th>
                <th className="py-2 pr-4 font-semibold">Creator Pro</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              <tr>
                <td className="py-3 pr-4 text-white/80">Posts included</td>
                <td className="py-3 pr-4 text-white">10 / month</td>
                <td className="py-3 pr-4 text-white">Unlimited</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 text-white/80">Vesting speed</td>
                <td className="py-3 pr-4 text-white">{12} months</td>
                <td className="py-3 pr-4 text-white">{6} months</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 text-white/80">AI credits</td>
                <td className="py-3 pr-4 text-white">{aiCredits.creator} / mo</td>
                <td className="py-3 pr-4 text-white">{aiCredits.creator_pro} / mo</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 text-white/80">Payout eligibility</td>
                <td className="py-3 pr-4 text-white">Yes</td>
                <td className="py-3 pr-4 text-white">Yes</td>
              </tr>
              <tr>
                <td className="py-3 pr-4 text-white/80">Priority features</td>
                <td className="py-3 pr-4 text-white/80">Standard rollout</td>
                <td className="py-3 pr-4 text-white/80">Early access where available</td>
              </tr>
            </tbody>
          </table>
        </div>
      </SettingsCard>

      <SettingsCard title="FAQ">
        <div className="space-y-2">
          <FaqItem
            q="Do PIXE gifts leave Pixeloop?"
            a="No. PIXE gifts stay on Pixeloop. Payouts are made in USD."
            open={faqOpen === 'stay'}
            onToggle={() => setFaqOpen((k) => (k === 'stay' ? null : 'stay'))}
          />
          <FaqItem
            q="How does vesting work?"
            a={
              <>
                Earnings unlock over time while your plan is active. Faster vesting means more of your earnings become
                available sooner.
              </>
            }
            open={faqOpen === 'vesting'}
            onToggle={() => setFaqOpen((k) => (k === 'vesting' ? null : 'vesting'))}
          />
          <FaqItem
            q="What happens if I cancel?"
            a="Vesting stops when your plan isn't active. Previously vested earnings remain available for withdrawal once Stripe is connected."
            open={faqOpen === 'cancel'}
            onToggle={() => setFaqOpen((k) => (k === 'cancel' ? null : 'cancel'))}
          />
          <FaqItem
            q="When can I withdraw?"
            a="You can withdraw once you have an available balance and you've connected Stripe."
            open={faqOpen === 'withdraw'}
            onToggle={() => setFaqOpen((k) => (k === 'withdraw' ? null : 'withdraw'))}
          />
          <FaqItem
            q="Do I need to connect Stripe?"
            a="Yes. Stripe is used to withdraw vested earnings. Connecting Stripe does not affect PIXE gifts, which stay on Pixeloop."
            open={faqOpen === 'stripe'}
            onToggle={() => setFaqOpen((k) => (k === 'stripe' ? null : 'stripe'))}
          />
        </div>
      </SettingsCard>
    </div>
  )
}
